import { roundDecimals } from "./formatNumbers"

const toSafeNumber = (value, country = null) => {
    if (typeof value === "number") return Number.isFinite(value) ? value : 0;
    if (typeof value !== "string") return 0;

    const cleaned = value.trim().replaceAll(/[^\d,.-]/g, "");
    if (!cleaned) return 0;

    if (country?.decimalSeparator && country?.thousandSeparator) {
        const { decimalSeparator, thousandSeparator } = country;
        const thousandRegex = new RegExp(`\\${thousandSeparator}`, "g");
        const decimalRegex = new RegExp(`\\${decimalSeparator}`, "g");

        const normalizedByCountry = cleaned
            .replaceAll(thousandRegex, "")
            .replaceAll(decimalRegex, ".");

        const parsedByCountry = Number(normalizedByCountry);
        return Number.isFinite(parsedByCountry) ? parsedByCountry : 0;
    }

    const lastComma = cleaned.lastIndexOf(",");
    const lastDot = cleaned.lastIndexOf(".");
    const hasComma = lastComma !== -1;
    const hasDot = lastDot !== -1;
    const decimalSeparator = hasComma && (!hasDot || lastComma > lastDot) ? "," : ".";

    const normalized = cleaned
        .replaceAll(decimalSeparator === "," ? "." : ",", "")
        .replaceAll(decimalSeparator, ".");

    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * 🇪🇨 ECUADOR - Ajuste de facturas con bug de datos (Business Central)
 * --------------------------------------------------------------
 * Esta función fue solicitada por el equipo de Ecuador como solución
 * temporal a un conjunto de facturas con errores en los descuentos
 * (importadas desde Business Central) que **no pueden ser modificadas
 * directamente en BC** debido a restricciones de sincronización.
 *
 * Descripción:
 * Corrige el total de una factura aplicando ajustes porcentuales
 * adicionales cuando se detectan diferencias conocidas de cálculo:
 *
 *  - Aplica un **4% adicional por línea** si la línea tiene un descuento > 4%.
 *    (casos en los que originalmente se había aplicado 54%, 44.55%, 8.35%, etc.)
 *
 *  - Mantiene el total original cuando la diferencia entre el total y el pago
 *    es menor o igual a ±0.02 (tolerancia numérica).
 *
 * Nota:
 * Este ajuste es solo para permitir que den los valores para crear una nota de credito

 * @param {Object} invoice        Factura seleccionada (con sus líneas)
 * @param {number} paymentTotal   Monto total pagado registrado en caja
 * @param {number} total          Total original calculado por el sistema
 * @param {number} pendingRaw     Diferencia entre total y total pagado
 * @param {Array} paymentLines    Líneas de pago asociadas
 *
 * @returns {Object} Objeto con:
 *  - {number} totalAjustado  → Total recalculado con los ajustes aplicados
 *  - {number} ajuste         → Diferencia detectada entre pago y total
 *
 **/
export const applyConditionalAdjustment = (invoice, paymentTotal, total, pendingRaw, paymentLines) => {
    // Normalizamos todos los valores a positivos
    const absPaymentTotal = Math.abs(paymentTotal || 0);
    const absTotal = Math.abs(total || 0);
    const absPending = Math.abs(pendingRaw || 0);

    if (!invoice || !invoice.lines || invoice.lines.length === 0) {
        return {
            totalAjustado: 0,
            ajuste: 0
        };
    }

    //si el total esta dentro de la tolerancia no se realizan cambios
    if (absPending <= 0.02) {
        return {
            totalAjustado: absTotal,
            ajuste: 0
        };
    }

    // Verificar si hay líneas con descuento > 4 %
    const hasExtraDiscount = invoice.lines.some(line => (line.Line_Discount_ || 0) > 4)
        || (invoice.lines.some(line => (line.Line_Discount_ || 0) >= 4)
            && paymentLines.length > 1);

    if (!hasExtraDiscount) {
        return { totalAjustado: absTotal, ajuste: 0 };
    }

    // Calcular la diferencia real entre pago y total
    const ajuste = Math.round((absPaymentTotal - absTotal) * 100) / 100;
    const totalAjustado = Math.round((absPaymentTotal - ajuste) * 100) / 100;

    return { totalAjustado, ajuste };
};

const getEmptyTotals = () => ({
    subtotal: 0,
    vat: 0,
    baseTotal: 0,
    total: 0,
    paymentTotal: 0,
    pending: 0,
    totalAjustado: 0,
    ajuste: 0,
    paymentMethodDiscount: 0,
});

/**
 * Calcula el precio unitario que se debe enviar a Business Central.
 *
 * IMPORTANTE:
 * - Business Central SIEMPRE espera el precio unitario ORIGINAL
 *   → SIN IVA
 *   → SIN DESCUENTOS aplicados
 *
 * - El IVA y los descuentos (% de línea y de cabecera) los recalcula
 *   Business Central internamente al recibir la factura.
 *
 * - Esta función NO debe usarse para mostrar importes en pantalla
 *   ni para imprimir tickets.
 *
 * - Si el precio original incluye IVA, aquí se elimina para obtener
 *   la base imponible real.
 *
 * @param {number} unitPrice - Precio unitario original (puede incluir IVA)
 * @param {number} ivaPct - Porcentaje de IVA del producto
 * @param {boolean} priceIncludesVAT - Indica si el precio incluye IVA
 * @returns {number} Precio unitario SIN IVA y SIN descuento
 */
export const calculateBCUnitPrice = ({
  unitPrice = 0,
  ivaPct = 0,
  priceIncludesVAT = false,
}) => {
  if (priceIncludesVAT) {
    return roundDecimals(unitPrice / (1 + ivaPct / 100));
  }
  return roundDecimals(unitPrice);
};

/**
 * Calcula los importes de una línea de factura o nota de crédito.
 * Soporta precios con y sin IVA.
 *
 * @param {Object} params
 * @param {number} params.unitPrice         Precio unitario
 * @param {number} params.qty               Cantidad (puede ser negativa)
 * @param {number} params.discountPct       Descuento en %
 * @param {number} params.ivaPct            IVA en %
 * @param {boolean} params.priceIncludesVAT Indica si el precio incluye IVA
 *
 * @returns {{
 *   base: number,   // Importe sin IVA
 *   vat: number,    // IVA calculado
 *   total: number   // Importe final con IVA
 * }}
 */
export const calculateLineAmounts = ({
    unitPrice = 0,
    qty = 0,
    discountPct = 0,
    ivaPct = 0,
    priceIncludesVAT = false,
    country = null,
}) => {
    const safeUnitPrice = toSafeNumber(unitPrice, country);
    const safeQty = toSafeNumber(qty, country);
    const safeDiscountPct = toSafeNumber(discountPct, country);
    const safeIvaPct = toSafeNumber(ivaPct, country);

    const discountedPrice = safeUnitPrice * (1 - safeDiscountPct / 100);
    const discount = (safeUnitPrice - discountedPrice) * safeQty;

    let base = 0;
    let vat = 0;
    let total = 0;

    if (priceIncludesVAT) {
        total = safeQty * discountedPrice;
        base = total / (1 + safeIvaPct / 100);
        vat = total - base;
    } else {
        base = safeQty * discountedPrice;
        vat = base * (safeIvaPct / 100);
        total = base + vat;
    }

    return {
        base: roundDecimals(base),
        vat: roundDecimals(vat),
        total: roundDecimals(total),
        discount: roundDecimals(discount),
    };
};

/** Calcula subtotal e IVA línea a línea
 * Soporta precios con y sin IVA (Price_Includes_VAT)
 */
const calculateLineTotals = (lines, country = null) => {
    let subtotal = 0;
    let vat = 0;

    lines.forEach((line) => {
        const { base, vat: lineVat } = calculateLineAmounts({
            unitPrice: line.Unit_Price,
            qty: line.Quantity,
            discountPct: line.Line_Discount_,
            ivaPct: line.PorIva,
            priceIncludesVAT: line.Price_Includes_VAT,
            country,
        });

        subtotal += base;
        vat += lineVat;
    });

    return {
        subtotal: roundDecimals(subtotal),
        vat: roundDecimals(vat),
    };
};

/** Descuento por método de pago según reglas */
const getPaymentMethodDiscount = (lines, paymentLines) => {
    const hasSpecialDiscount = lines.some((l) => (l.Line_Discount_ || 0) > 0);

    if (hasSpecialDiscount) return 0;
    if (paymentLines.length === 0) return 0;
    if (paymentLines.length > 1) return 0;

    return paymentLines[0].Dto || 0;
};

/** Aplica descuento (si hay) y calcula totales base */
const calculateBaseTotals = (subtotal, vat, paymentMethodDiscount) => {
    const baseTotal = subtotal + vat;
    let total = subtotal + vat;

    if (paymentMethodDiscount > 0) {
        total = total * (1 - paymentMethodDiscount / 100);
    }

    return {
        baseTotal: roundDecimals(baseTotal),
        total: roundDecimals(total),
    };
};

/** Calcula total pagado y pendiente */
const calculatePaymentSummary = (paymentLines, total, country = null) => {
    const paymentTotal = roundDecimals(
        paymentLines.reduce((sum, l) => sum + toSafeNumber(l.Amount, country), 0)
    );
    const pending = roundDecimals(total - paymentTotal);

    return { paymentTotal, pending };
};

/**
 * Calcula todos los totales de una factura o nota de crédito.
 * Centraliza la lógica de cálculo (subtotal, IVA, descuento, ajuste)

 * 🇪🇨 Incluye la corrección del 4 % solicitada por Ecuador
 * para facturas con diferencias de redondeo
 *
 * @param {Object} invoice Factura o nota de crédito completa (con lines[])
 * @param {Array} paymentLines Líneas de pago asociadas
 * @returns {{
 *   subtotal: number,         
 *     → Total neto de las líneas sin IVA (ya con descuentos de línea aplicados).
 *
 *   vat: number,              
 *     → Total del impuesto IVA calculado sobre el subtotal.
 *
 *   baseTotal: number,        
 *     → Subtotal + VAT sin incluir descuento.
 *
 *   total: number,            
 *     → Total bruto del documento: subtotal + IVA.
 *       Si existe descuento por método de pago, el total reflejará el valor neto.
 *
 *   paymentTotal: number,     
 *     → Suma de todos los importes abonados en las líneas de pago.
 *
 *   pending: number,          
 *     → Diferencia entre el total del documento y el total pagado.
 *       Si está dentro de ±0.02, se considera tolerancia (sin ajuste).
 *
 *   totalAjustado: number,    
 *     → Total corregido después de aplicar el ajuste del 4 % 
 *       (solo en los casos donde se detectan descuentos erróneos).
 *
 *   ajuste: number,           
 *     → Diferencia exacta entre el total calculado y el total pagado.
 *       Representa el importe del ajuste aplicado (positivo o negativo).
 * }}
 */
export const calculateInvoiceTotalsHelper = (invoice, paymentLines = [], country = null) => {
    if (!invoice || !invoice.lines) return getEmptyTotals();

    // Calcular subtotal e IVA línea a línea
    const { subtotal, vat } = calculateLineTotals(invoice.lines, country);

    // Determinar descuento de método de pago según reglas de Ecuador
    const paymentMethodDiscount = getPaymentMethodDiscount(invoice.lines, paymentLines);

    // Calcular totales base y aplicar descuento (si aplica)
    const { baseTotal, total } = calculateBaseTotals(subtotal, vat, paymentMethodDiscount);

    // Calcular totales de pago y pendiente
    const { paymentTotal, pending } = calculatePaymentSummary(paymentLines, Math.abs(total), country);

    // Aplicar corrección del 4 % (Ecuador)
    const { totalAjustado, ajuste } = applyConditionalAdjustment(
        invoice,
        paymentTotal,
        total,
        pending,
        paymentLines
    );

    return {
        subtotal,
        vat,
        baseTotal,
        total,
        paymentTotal,
        pending,
        totalAjustado,
        ajuste,
        paymentMethodDiscount,
    };
};
